import React, { useEffect, useState } from 'react';
import { Box, Dialog } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { ColorPalette } from '../../styles/constants';
import { EmployeeAllocations, ReportFilters } from '../../models/reports';
import { getEmployeesAllocations } from '../../services/api/reportsServices';
import AppLoading from '../app/appLoading';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { ReportsInputsData } from './reportsInputsData';
import { EmployeeAllocationsCard } from './employeeAllocationsCard';
import { ROLES_ID } from '../../models/employee';
import { useReportsViewMode } from '../../context/reports/useReportsCtx';
import { format, parseISO } from 'date-fns';

const styles = {
  dialog: {
    maxWidth: '800px',
    overflowX: 'hidden',
    position: 'relative',
  },
  topBox: {
    p: 3,
    background: ColorPalette.BACKGROUND,
  },
  inputDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mb: 2,
  },
  inputValue: {
    color: ColorPalette.RAISIN_BLACK,
  },
  allocationsListBox: {
    maxHeight: '400px',
  },
};

interface AllocationsModalProps {
  isOpen: boolean;
  handleClose: () => void;
  filters: ReportFilters;
}

export function EmployeeAllocationsModal({
  isOpen,
  handleClose,
  filters,
}: AllocationsModalProps): JSX.Element {
  const [viewMode] = useReportsViewMode();
  const { onErrorSnackbar } = useAppSnackbar();

  const {
    managerId,
    employeeId,
    startDate,
    endDate,
    projectId,
    showHierarchy,
    includeInactive,
  } = filters;

  const [employeesAllocations, setEmployeesAllocations] = useState<
    EmployeeAllocations[] | null
  >(null);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      const startDateParsed = parseISO(startDate);
      const endDateParsed = parseISO(endDate);

      const filteringPayload = {
        startDate: format(startDateParsed, 'yyyy-MM-dd'),
        endDate: format(endDateParsed, 'yyyy-MM-dd'),
        employeeId,
        projectId,
        managerId: managerId || null,
        userRoleId: ROLES_ID.TEAM_LEAD,
        showHierarchy: showHierarchy || false,
        includeInactive: includeInactive || false,
        includeDeleted: includeInactive || false,
      };

      getEmployeesAllocations(filteringPayload)
        .then((resp) => {
          setEmployeesAllocations(resp.items);
        })
        .catch(onErrorSnackbar)
        .finally(() => setIsLoading(false));
    }
  }, [
    employeeId,
    endDate,
    filters,
    isOpen,
    managerId,
    onErrorSnackbar,
    projectId,
    showHierarchy,
    startDate,
    includeInactive,
  ]);

  return (
    <Dialog open={isOpen} PaperProps={{ sx: styles.dialog }}>
      <AppDialogTitle
        title="Allocations"
        handleClose={handleClose}
        sx={{ position: 'sticky', top: 0 }}
      />
      <Box sx={styles.topBox}>
        <ReportsInputsData filters={filters} viewMode={viewMode} />
        <Box sx={styles.allocationsListBox}>
          {isLoading && <AppLoading />}
          {!isLoading &&
            employeesAllocations?.map((employeeAllocation) => {
              return (
                <EmployeeAllocationsCard
                  employeeAllocations={employeeAllocation}
                  key={employeeAllocation.employee.id}
                />
              );
            })}
        </Box>
      </Box>
    </Dialog>
  );
}
